import { Component, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { GaFormlyService, IExtraFieldConfigurationObject } from '../../shared/formly/ga-formly.service';
import { TitleService } from '../../shared/services/title.service';
import { TranslateService } from '@ngx-translate/core';
import { StateService, Transition } from '@uirouter/angular';
import { util } from '../../shared/util';
import { STATE_HOME_PAGE } from '../../shared/app-states';
import { GaValidateService } from '../../shared/formly/ga-validate.service';
import { AccessDataService, ISetPasswordResponse } from '../access-data.service';
import { AuthService } from '../../shared/services/auth/auth.service';

@Component({
  selector: 'reset-password',
  template: `
	  <h2 class="title" translate>APP.ACCESS.PASSWORD.RESET_PASSWORD</h2><br />

	  <div class="alert alert-danger login-failure" *ngIf="resetPasswordFailure">
		  <i class="fa fa-exclamation-circle"></i>
		  <span translate>{{resetPasswordFailure}}</span>
	  </div>

	  <div *ngIf="!resetPasswordFailure">
			<formly [form]="form" [fields]="fields" [model]="model" [noValidate]="true">
				<button class="btn" (click)="reset()"
					[disabled]="formInvalid" [busyButton]="resetting">
					<span translate="APP.ACCESS.PASSWORD.UPDATE_PASSWORD"></span>
				</button>
				<return-to-login class="d-inline"></return-to-login>
			</formly>
	  </div>
	`
})
export class ResetPasswordComponent implements OnInit {
  public form:UntypedFormGroup = new UntypedFormGroup({});
  public fields:IExtraFieldConfigurationObject[];
  public resetPassword:boolean;
  public resetPasswordFailure:string;
  public resetting:boolean;
  public model:{
    password?:string;
    confirmPassword?:string;
  } = {};
  public formInvalid:boolean = true;

  constructor(
    private formlyService:GaFormlyService,
    private validateService:GaValidateService,
    private accessDataService:AccessDataService,
    private authService:AuthService,
    private transition:Transition,
    private translateService:TranslateService,
    private stateService:StateService,
    titleService:TitleService
  ) {
    titleService.setTitle(translateService.instant('APP.ACCESS.PASSWORD.RESET_PASSWORD'));
  }

  public ngOnInit():void {
    this.fields = this.createFields();

    if (util.isNullOrUndefined(this.transition.params()?.token)) {
      this.resetPasswordFailure = 'APP.ACCESS.PASSWORD.ERROR_RESETTING';
    }

    // add listener
    this.listen();
  }

  private listen():void {
    this.form.statusChanges.subscribe(() => {
      this.formInvalid = this.form.invalid;
    });
  }

  public reset():void {
    // show form errors when the reset password form is invalid
    this.validateService.validateFields(this.fields, this.model, true)
      .then(() => {
        this.resetting = true;
        if (this.transition.params()?.token) {
          this.accessDataService.setPassword(this.model.password)
            .then((response:ISetPasswordResponse) => {
              this.resetPassword = true;
              if (response?.token) {
                this.authService.setToken(response.token, true);
                this.authService.authenticateAgainstServer();
                this.stateService.go(STATE_HOME_PAGE);
              }
            })
            .finally(() => {
              this.resetting = false;
            });
        }
      })
      .catch(util.noop);
  }

  private createFields():IExtraFieldConfigurationObject[] {
    const labelClass:string = 'col-sm-5';
    const valueClass:string = 'col-sm-7';

    const passwordField:IExtraFieldConfigurationObject = this.formlyService.anInput('password', 'APP.ACCESS.PASSWORD_TEXT')
      .isRequired()
      .withLabelClass(labelClass)
      .withValueClass(valueClass)
      .withInputType('password')
      .withMinLength(6)
      .build();

    return [
      passwordField,

      this.formlyService.anInput('confirmPassword', 'APP.ACCESS.PASSWORD.CONFIRM_PASSWORD_TEXT')
        .isRequired()
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .withInputType('password')
        .withMinLength(6)
        .withExtraOptions({
          validators: {
            format: {
              expression: (control:UntypedFormControl) => {
                const value = control.value;
                return passwordField.formControl?.value === value;
              },
              message: this.translateService.instant('APP.ACCESS.PASSWORD.PASSWORDS_NOT_MATCH')
            }
          }
        })
        .build()
    ];
  }
}